import { motion, useScroll, useTransform } from "framer-motion";
import { portraits, activePortraitId } from "../../data/portfolio";

/**
 * Reusable hero portrait renderer.
 * Swap the hero image by changing `activePortraitId` in /src/data/portfolio.js
 * or by passing explicit `src` / `focus` props.
 *
 * Preserves the composition (no aggressive zoom or crop), applies a natural cinematic
 * grade with a subtle brightness lift, and layers gentle vignettes for legibility.
 */
export const HeroPortrait = ({
  src,
  focus,
  alt = "Portrait",
  brighten = 1.08,
}) => {
  const active =
    portraits.find((p) => p.id === activePortraitId) || portraits[0];
  const resolvedSrc = src || active.src;
  const resolvedFocus = focus || active.focus || "center 22%";

  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, -50]);
  const scale = useTransform(scrollY, [0, 900], [1, 1.03]);

  // Soft radial mask blends every edge of the portrait into the dark background,
  // dissolving the rectangular frame while keeping the subject fully visible.
  const softMask =
    "radial-gradient(ellipse 68% 82% at 55% 46%, #000 42%, rgba(0,0,0,0.75) 62%, rgba(0,0,0,0.35) 82%, transparent 100%)";

  return (
    <div className="absolute inset-0 flex items-center justify-end pointer-events-none overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2, ease: [0.19, 1, 0.22, 1] }}
        style={{ y, scale }}
        className="relative h-[112vh] aspect-[4/5] -translate-x-6 sm:-translate-x-12 lg:-translate-x-20"
      >
        {/* Gentle floating loop */}
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0"
          style={{
            WebkitMaskImage: softMask,
            maskImage: softMask,
            WebkitMaskRepeat: "no-repeat",
            maskRepeat: "no-repeat",
            WebkitMaskSize: "100% 100%",
            maskSize: "100% 100%",
          }}
        >
          <img
            key={resolvedSrc}
            src={resolvedSrc}
            alt={alt}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
            style={{
              objectPosition: resolvedFocus,
              filter: `contrast(1.06) brightness(${brighten}) saturate(0.95)`,
              imageRendering: "auto",
            }}
          />

          {/* Soft left fade for text legibility */}
          <div
            className="absolute inset-y-0 left-0 w-[55%] pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050505 0%, rgba(5,5,5,0.65) 30%, rgba(5,5,5,0.18) 60%, transparent 95%)",
            }}
          />

          {/* Warm ambient tint — very light so original colors read naturally */}
          <div
            className="absolute inset-0 pointer-events-none mix-blend-soft-light opacity-30"
            style={{
              background:
                "linear-gradient(160deg, rgba(200,162,91,0.20) 0%, transparent 50%, rgba(28,42,58,0.22) 100%)",
            }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};
